import React, { useReducer, useEffect, useContext, useCallback, useRef } from 'react';
import { Dialog } from '@progress/kendo-react-dialogs';
import { Button } from '@progress/kendo-react-buttons';
import { ProgressBar } from '@progress/kendo-react-progressbars';
import { trashIcon } from '@progress/kendo-svg-icons';
import { SvgIcon } from '@progress/kendo-react-common';
import config from '../config/config';
import { AppContext } from '../AppContext';

const MAX_FILE_SIZE = 5 * 1024 * 1024;
const STATUSES = { QUEUED:'queued', UPLOADING:'uploading', FINALIZING:'finalizing', DONE:'done' };
const STATUS_COLORS = {
  [STATUSES.QUEUED]:    '#888888',
  [STATUSES.UPLOADING]: '#f5811e',
  [STATUSES.FINALIZING]:'#0000FF',
  [STATUSES.DONE]:      '#000000'
};

function filesReducer(state, action) {
  switch (action.type) {
    case 'SET_FILES':   return action.payload;
    case 'ADD_FILES':   return [...state, ...action.payload];
    case 'UPDATE_FILE': return state.map(f => f.id === action.payload.id ? { ...f, ...action.payload } : f);
    case 'REMOVE_FILE': return state.filter(f => f.id !== action.payload.id);
    default:            return state;
  }
}

export default function JobDocumentsModal({ visible, onClose, jobID }) {
  const { showNotification } = useContext(AppContext);
  const fileInputRef = useRef(null);
  const [files, dispatch] = useReducer(filesReducer, []);

  /* 🔹 1: immer aktuelle Liste in einer Ref halten */
  const filesRef = useRef([]);
  useEffect(() => { filesRef.current = files; }, [files]);

  const animatedRef = useRef({});
  const uuid = () => Math.random().toString(36).substring(2);

  const toFileObj = (url, idx) => ({
    id: `init-${idx}`,
    name: decodeURIComponent(url.split('/').pop()),
    url,
    progress: 100,
    status: STATUSES.DONE
  });

  const loadDocs = useCallback(async () => {
    try {
      const res = await fetch(`${config.host}/jobs/${encodeURIComponent(jobID)}`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const { JobDocuments } = await res.json();
      const urls = Array.isArray(JobDocuments) ? JobDocuments.filter(Boolean) : [];
      dispatch({ type: 'SET_FILES', payload: urls.map((u, i) => toFileObj(u, i)) });
    } catch (err) {
      console.error(err);
      showNotification('error', `Dokumente laden fehlgeschlagen: ${err.message}`);
    }
  }, [jobID, showNotification]);

  useEffect(() => { if (visible) loadDocs(); }, [visible, loadDocs]);

  const persist = useCallback(async urls => {
    const unique = Array.from(new Set(urls));
    await fetch(`${config.host}/jobs/${encodeURIComponent(jobID)}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ JobDocuments: unique })
    });
  }, [jobID]);

  const handleClose = () =>
    onClose(filesRef.current.map(f => f.url).filter(Boolean));                        /* 🔹 2 */

  const handleFiles = list => {
    const arr = Array.from(list);
    arr.forEach(f => {
      if (f.size > MAX_FILE_SIZE) {
        showNotification('error', `Die Datei "${f.name}" ist zu groß. Maximal 5 MB.`);
      }
    });

    const fresh = arr
      .filter(f => f.size <= MAX_FILE_SIZE)
      .map(f => ({ id: uuid(), name: f.name, file: f, progress: 0, status: STATUSES.QUEUED }));

    if (fresh.length) {
      dispatch({ type: 'ADD_FILES', payload: fresh });
      fresh.forEach(uploadEntry);
    }
  };

  /* Upload ---------------------------------------------------- */
  const uploadEntry = useCallback(entry => {
    dispatch({ type:'UPDATE_FILE', payload:{ id:entry.id, status:STATUSES.UPLOADING, progress:0 } });

    const form = new FormData();
    form.append('file', entry.file, entry.name);

    const xhr = new XMLHttpRequest();
    xhr.open('POST', `${config.host}/upload-jobdocuments/${encodeURIComponent(jobID)}`);
    xhr.upload.onprogress = e => {
      if (!e.lengthComputable) return;
      dispatch({ type:'UPDATE_FILE', payload:{ id:entry.id, progress:Math.round(e.loaded*100/e.total) } });
    };
    xhr.onload = async () => {
      if (xhr.status < 200 || xhr.status >= 300) {
        dispatch({ type:'REMOVE_FILE', payload:{ id:entry.id } });
        showNotification('error', `Upload "${entry.name}" fehlgeschlagen (${xhr.status})`);
        return;
      }
      let data; try { data = JSON.parse(xhr.responseText); } catch { data = {}; }
      const urls = Array.isArray(data.urls) ? data.urls : data.url ? [data.url] : [];
      if (!urls.length) {
        dispatch({ type:'REMOVE_FILE', payload:{ id:entry.id } });
        showNotification('error', `Keine URL für "${entry.name}" erhalten`);
        return;
      }

      dispatch({
        type:'UPDATE_FILE',
        payload:{ id:entry.id, url:urls[0], progress:100, status:STATUSES.DONE }
      });

      /* 🔹 3: komplette DONE-Liste + aktuelle URL speichern */
      const doneUrls = filesRef.current
        .filter(f => f.status === STATUSES.DONE)
        .map(f => f.url)
        .concat(urls[0]);

      try {
        await persist(doneUrls);
        showNotification('success', `"${entry.name}" hochgeladen.`);
      } catch (err) {
        console.error(err);
        showNotification('error', `Speichern gescheitert: ${err.message}`);
      }
    };
    xhr.onerror = () => {
      dispatch({ type:'REMOVE_FILE', payload:{ id:entry.id } });
      showNotification('error', `Netzwerkfehler bei "${entry.name}"`);
    };
    xhr.send(form);
  }, [jobID, persist, showNotification]);                                            /* 🔹 4  – `files` entfernt */

  /* Löschen --------------------------------------------------- */
  const deleteFile = useCallback(async entry => {
    try {
      await fetch(`${config.host}/delete-jobdocument/${encodeURIComponent(jobID)}`, {
        method:'DELETE',
        headers:{ 'Content-Type':'application/json' },
        body:JSON.stringify({ url:entry.url })
      });
      dispatch({ type:'REMOVE_FILE', payload:{ id:entry.id } });

      const remain = filesRef.current
        .filter(f => f.id !== entry.id)
        .map(f => f.url)
        .filter(Boolean);

      await persist(remain);
      showNotification('success', `"${entry.name}" gelöscht.`);
    } catch (err) {
      console.error(err);
      showNotification('error', `Löschen gescheitert: ${err.message}`);
    }
  }, [jobID, persist, showNotification]);

  if (!visible) return null;

  const truncate = s => (s.length > 50 ? s.slice(0, 47) + '…' : s);
  const onHover  = e => (e.currentTarget.style.textDecoration = 'underline');
  const onLeave  = e => (e.currentTarget.style.textDecoration = 'none');

  return (
    <Dialog title="Jobdokumente" width={640} onClose={handleClose}>
      <div style={{ padding: 20 }}>
        {/* Drop-Zone */}
        <div
          onDrop={e => { e.preventDefault(); handleFiles(e.dataTransfer.files); }}
          onDragOver={e => e.preventDefault()}
          style={{ border:'2px dashed #888', padding:50, borderRadius:4,
                   textAlign:'center', marginBottom:20 }}
        >
          Dateien hierhin ziehen oder{' '}
          <Button onClick={() => fileInputRef.current.click()}>auswählen</Button>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            style={{ display:'none' }}
            onChange={e => handleFiles(e.target.files)}
          />
        </div>

        {/* Tabelle */}
        <div style={{
          display:'grid', gridTemplateColumns:'3fr 1fr 2fr 40px',
          gap:'8px 16px', alignItems:'center', fontSize:12
        }}>
          <div style={{ fontWeight:'bold' }}>Datei</div>
          <div style={{ fontWeight:'bold' }}>Status</div>
          <div style={{ fontWeight:'bold' }}>Fortschritt</div>
          <div />

          {files.map(f => {
            const isDone       = f.status === STATUSES.DONE;
            const alreadyAnim  = animatedRef.current[f.id];
            if (isDone && !alreadyAnim) animatedRef.current[f.id] = true;

            return (
              <React.Fragment key={f.id}>
                {/* Name / Link */}
                <div title={f.name} style={{
                  whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis',
                  color:'#f5811e', cursor:'pointer'
                }} onMouseEnter={onHover} onMouseLeave={onLeave}>
                  {isDone && f.url ? (
                    <a href={f.url} target="_blank" rel="noopener noreferrer"
                       style={{ fontSize:12, color:'#f5811e', textDecoration:'none' }}>
                      {truncate(f.name)}
                    </a>
                  ) : (
                    <span style={{ fontSize:12 }}>{truncate(f.name)}</span>
                  )}
                </div>

                {/* Status */}
                <div style={{ color:STATUS_COLORS[f.status] || '#666', fontSize:12 }}>
                  {f.status === STATUSES.QUEUED     && 'In Warteschlange'}
                  {f.status === STATUSES.UPLOADING  && 'Uploading'}
                  {f.status === STATUSES.FINALIZING && 'Finalizing'}
                  {f.status === STATUSES.DONE       && 'Done'}
                </div>

                {/* Progress */}
                <div style={{ width:'100%' }}>
                  <ProgressBar
                    value={isDone ? 100 : f.progress}
                    animation={isDone && !alreadyAnim}
                    showValue={false}
                    style={{ width:'100%' }}
                  />
                </div>

                {/* Delete */}
                <div>
                  <Button fillMode="flat" title="Löschen" onClick={() => deleteFile(f)}>
                    <SvgIcon icon={trashIcon} />
                  </Button>
                </div>
              </React.Fragment>
            );
          })}
        </div>

        <p style={{ marginTop:20 }}><strong>Hinweis:</strong> Max. 5 MB pro Datei.</p>
        <div style={{ display:'flex', justifyContent:'flex-end', marginTop:15 }}>
          <Button onClick={handleClose}>Schließen</Button>
        </div>
      </div>
    </Dialog>
  );
}
